import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Filter, Grid, List, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ProductCard from '../components/ProductCard';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState('grid');

  const currentPage = parseInt(searchParams.get('page')) || 1;
  const selectedCategory = searchParams.get('category') || '';
  const searchQuery = searchParams.get('search') || '';
  const sortBy = searchParams.get('sort') || 'newest';
  const vehicleBrand = searchParams.get('vehicle_brand') || '';
  const vehicleModel = searchParams.get('vehicle_model') || '';

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [currentPage, selectedCategory, searchQuery, sortBy, vehicleBrand, vehicleModel]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 24,
        sort: sortBy
      });
      if (selectedCategory) params.append('category', selectedCategory);
      // Build search query from vehicle brand/model or text query
      const searchTerms = [];
      if (searchQuery) searchTerms.push(searchQuery);
      if (vehicleBrand) searchTerms.push(vehicleBrand);
      if (vehicleModel) searchTerms.push(vehicleModel);
      if (searchTerms.length > 0) params.append('search', searchTerms.join(' '));

      const res = await axios.get(`${API}/all-products?${params}`);
      setProducts(res.data.products || []);
      setTotalProducts(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
      console.log('API Response:', res.data.total, res.data.total_pages);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage);
    setSearchParams(params);
    window.scrollTo(0, 0);
  };

  const handleCategoryChange = (catId) => {
    const params = new URLSearchParams(searchParams);
    if (catId) {
      params.set('category', catId);
    } else {
      params.delete('category');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleSortChange = (value) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort', value);
    params.set('page', '1');
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="products-page">
      <div className="container mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <Link to="/" className="hover:text-orange-500">Ana Sayfa</Link>
          <span>/</span>
          <span className="text-slate-900">Ürünler</span>
        </nav>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {searchQuery ? `"${searchQuery}" için sonuçlar` : 'Tüm Ürünler'}
            </h1>
            <p className="text-slate-500">{totalProducts.toLocaleString('tr-TR')} ürün bulundu</p>
          </div>

          <div className="flex items-center gap-4">
            <Select value={sortBy} onValueChange={handleSortChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sıralama" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">En Yeni</SelectItem>
                <SelectItem value="price_asc">Fiyat (Düşük)</SelectItem>
                <SelectItem value="price_desc">Fiyat (Yüksek)</SelectItem>
                <SelectItem value="name_asc">İsim (A-Z)</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'bg-orange-500' : ''}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-orange-500' : ''}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar - Categories */}
          <aside className="hidden md:block w-64 flex-shrink-0">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-3">Kategoriler</h3>
              <div className="space-y-1">
                <button
                  onClick={() => handleCategoryChange('')}
                  className={`w-full text-left px-3 py-2 rounded text-sm ${
                    !selectedCategory ? 'bg-orange-100 text-orange-700' : 'hover:bg-slate-100'
                  }`}
                >
                  Tüm Kategoriler
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`w-full text-left px-3 py-2 rounded text-sm ${
                      selectedCategory === cat.id ? 'bg-orange-100 text-orange-700' : 'hover:bg-slate-100'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 animate-pulse">
                    <div className="aspect-square bg-slate-200 rounded-lg mb-4" />
                    <div className="h-4 bg-slate-200 rounded mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : products.length > 0 ? (
              <>
                <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'}`}>
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-center gap-2 mt-8 py-4 bg-white rounded-lg shadow-sm" data-testid="pagination-container">
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    data-testid="pagination-prev"
                  >
                    Önceki
                  </Button>

                  <div className="flex items-center gap-1">
                    {currentPage > 2 && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => handlePageChange(1)}>1</Button>
                        {currentPage > 3 && <span className="px-2">...</span>}
                      </>
                    )}
                    {currentPage > 1 && (
                      <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)}>{currentPage - 1}</Button>
                    )}
                    <Button variant="default" size="sm" className="bg-orange-500">{currentPage}</Button>
                    {currentPage < totalPages && (
                      <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)}>{currentPage + 1}</Button>
                    )}
                    {currentPage < totalPages - 1 && (
                      <>
                        {currentPage < totalPages - 2 && <span className="px-2">...</span>}
                        <Button variant="outline" size="sm" onClick={() => handlePageChange(totalPages)}>{totalPages}</Button>
                      </>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    data-testid="pagination-next"
                  >
                    Sonraki
                  </Button>
                </div>

                <p className="text-center text-sm text-slate-500 mt-4">
                  {totalProducts.toLocaleString('tr-TR')} ürün
                </p>
              </>
            ) : (
              <div className="text-center py-16">
                <Filter className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-600 mb-2">Ürün bulunamadı</h3>
                <p className="text-slate-500">Farklı filtreler deneyin veya arama yapın</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
